import { JSX } from 'react';
import { Alert, Box, Chip, CircularProgress, Skeleton, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import {
    AdminMiniResultSummary,
    MiniRiskBand,
    useAdminMiniResultSummary,
} from '../hooks/admin-mini-result-summary';

export interface AdminMiniScoreCardProps {
    systemCode: string;
    runId?: number | string;
    resultId?: number | string;
}

/**
 * How each band is worded and coloured. The words are the portal's, so an admin and a customer
 * looking at the same result read the same verdict.
 */
const BANDS: Record<MiniRiskBand, { label: string, colour: 'error' | 'warning' | 'success' | 'default' }> = {
    high: { label: 'High risk', colour: 'error' },
    medium: { label: 'Medium risk', colour: 'warning' },
    low: { label: 'Low risk', colour: 'success' },
    pending: { label: 'Not graded yet', colour: 'default' },
};

/** What a figure reads while the result it describes has not been graded. */
const NOT_COUNTED_YET: string = '--';

function bandIcon(band: MiniRiskBand): JSX.Element {
    if (band === 'low') {
        return <CheckCircleOutlineIcon fontSize="small" />;
    }
    if (band === 'medium') {
        return <WarningAmberIcon fontSize="small" />;
    }
    if (band === 'pending') {
        return <HourglassEmptyIcon fontSize="small" />;
    }

    return <ErrorOutlineIcon fontSize="small" />;
}

/**
 * How one result was graded, drawn the way the customer portal draws it.
 *
 * The portal's `MiniResultsTopBlock` is the reference: a risk pill, a count of issues, and a gauge
 * of the share that passed. **The figures come from the same arithmetic** - `MiniGradeTally` and
 * `MiniRiskBand` on the API side, through one admin summary route - so a result cannot read as low
 * risk on one screen and medium on the other.
 *
 * **The gauge is drawn from the counts, not from the rounded percentage beside it.** A result at
 * 66.5% rounds to 67 and still bands as medium; colouring the ring from the rounded figure would
 * put a green ring around an amber verdict.
 */
export const AdminMiniScoreCard: (props: AdminMiniScoreCardProps) => JSX.Element =
    (props: AdminMiniScoreCardProps): JSX.Element => {
        const { summary, loading, error } = useAdminMiniResultSummary(
            props.systemCode,
            props.runId,
            props.resultId
        );

        if (error !== '') {
            return <Alert severity="error" sx={ { fontSize: '13px' } }>{ error }</Alert>;
        }

        if (loading || summary === null) {
            return (
                <Box sx={ { display: 'flex', gap: '16px', alignItems: 'center' } }>
                    <Skeleton variant="circular" width={ 96 } height={ 96 } />
                    <Box sx={ { flex: 1 } }>
                        <Skeleton variant="rounded" height={ 26 } width={ 120 } />
                        <Skeleton variant="text" width={ 160 } sx={ { marginTop: '8px' } } />
                    </Box>
                </Box>
            );
        }

        const band: MiniRiskBand = summary.riskBand ?? 'pending';
        const issues: number = summary.fail + summary.catastrophe;
        const graded: boolean = summary.totalGraded > 0;

        return (
            <Box sx={ {
                display: 'flex',
                alignItems: 'center',
                gap: '20px',
                flexWrap: 'wrap',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '12px',
                padding: '16px 18px',
            } }>
                <Box sx={ { position: 'relative', display: 'inline-flex' } }>
                    { /* The track behind the ring, so a low share still reads as a ring rather
                         than as a stray arc floating in space. */ }
                    <CircularProgress variant="determinate"
                                      value={ 100 }
                                      size={ 96 }
                                      thickness={ 4 }
                                      sx={ { color: 'action.hover' } } />
                    <CircularProgress variant="determinate"
                                      value={ graded ? summary.health : 0 }
                                      size={ 96 }
                                      thickness={ 4 }
                                      color={ BANDS[band].colour === 'default' ? 'inherit' : BANDS[band].colour }
                                      sx={ { position: 'absolute', left: 0 } } />
                    <Box sx={ {
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                    } }>
                        <Typography sx={ { fontSize: '22px', fontWeight: 700, lineHeight: 1 } }>
                            { graded ? `${ summary.health }%` : NOT_COUNTED_YET }
                        </Typography>
                        <Typography sx={ { fontSize: '10px', color: 'text.secondary' } }>
                            { 'passed' }
                        </Typography>
                    </Box>
                </Box>

                <Box sx={ { minWidth: '180px' } }>
                    <Chip size="small"
                          icon={ bandIcon(band) }
                          label={ BANDS[band].label }
                          color={ BANDS[band].colour }
                          variant={ band === 'pending' ? 'outlined' : 'filled' } />

                    <Typography sx={ { fontSize: '13px', color: 'text.secondary', marginTop: '10px' } }>
                        { graded
                            ? `${ issues } ${ issues === 1 ? 'issue' : 'issues' } detected`
                            : `${ NOT_COUNTED_YET } issues detected` }
                    </Typography>

                    <Typography sx={ { fontSize: '12px', color: 'text.secondary', marginTop: '2px' } }>
                        { graded
                            ? `${ summary.totalGraded } ${ summary.totalGraded === 1 ? 'test' : 'tests' } graded`
                            : 'Nothing graded yet' }
                    </Typography>
                </Box>

                { /* The three counts behind the ring. A pass total on its own hides the difference
                     between one fail and one catastrophe, which are not the same finding. */ }
                <Box sx={ { display: 'flex', gap: '10px', flexWrap: 'wrap' } }>
                    <Tally label="Passed" value={ summary.pass } tone="success.main" />
                    <Tally label="Failed" value={ summary.fail } tone="warning.main" />
                    <Tally label="Catastrophe" value={ summary.catastrophe } tone="error.main" />
                </Box>
            </Box>
        );
    };

const Tally: (props: { label: string, value: number, tone: string }) => JSX.Element =
    (props: { label: string, value: number, tone: string }): JSX.Element => (
        <Box sx={ {
            minWidth: '82px',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '8px',
            padding: '8px 10px',
        } }>
            <Typography sx={ { fontSize: '20px', fontWeight: 700, lineHeight: 1.1, color: props.tone } }>
                { props.value }
            </Typography>
            <Typography sx={ {
                fontSize: 'var(--wf-eyebrow)',
                letterSpacing: 'var(--wf-track)',
                textTransform: 'uppercase',
                color: 'text.secondary',
                fontWeight: 700,
            } }>
                { props.label }
            </Typography>
        </Box>
    );
